<script setup>
import { ref, defineEmits, watch, onMounted, onUnmounted , computed} from "vue";
import ArgonButton from "@/components/ArgonButton.vue";
import { useRouter, useRoute } from "vue-router";
import Swal from "sweetalert2";
import RegisterUser from "@/views/auth/domain/register";
// import Choices from "choices.js";
import { useStore } from "vuex";
import axios from "axios";
import { AuthClient } from "@/views/auth/data/auth-client";

const emit = defineEmits(["close"]);
const router = useRouter();
const route = useRoute();
const store = useStore();

const authClient = new AuthClient();

// Variables reactivas
const activeStep = ref(0);
const name = ref("");
const lastName = ref("");
const email = ref("");
const telefonoNumero = ref("");
const rolId = ref(3);

const errors = ref([]);
const message = ref("");
const loading = ref(false);
const password = ref("");
const confirmPassword = ref("");

const verificationCode = ref("");
const timer = ref(60);
let intervalId = null;
const codeSent = ref(false);
const codeError = ref("");
const codeLoading = ref(false);
const codeVerified = ref(false);
const resendLoading = ref(false);

// Updated roles to match your DTO (using numbers for role_id)
const roles = ref([
  { id: 3, nombre: "Instructor" }, // Assuming 1 is for admin
  { id: 4, nombre: "Estudiante" },  // Assuming 2 is for regular user
]);

// Password mismatch watcher
watch([password, confirmPassword], () => {
  errors.value = errors.value.filter(e => e !== "Las contraseñas no coinciden.");
  if (password.value !== confirmPassword.value) {
    if (!errors.value.includes("Las contraseñas no coinciden.")) {
      errors.value.push("Las contraseñas no coinciden.");
    }
    invalidFields.value.confirmPassword = true;
  } else {
    invalidFields.value.confirmPassword = false;
  }
});

const onNextClick = async () => {
  // Si estás en el paso 2, primero haces el envío (handleSubmit)
  if (activeStep.value === 2) {
    await handleSubmit();
  }
  // Luego avanzas al siguiente paso
  nextStep();
};

const formattedTimer = computed(() => {
  const minutes = String(Math.floor(timer.value / 60)).padStart(2, '0');
  const seconds = String(timer.value % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
});

const clearTimer = () => {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
};

const startTimer = () => {
  clearTimer();
  intervalId = setInterval(() => {
    if (timer.value > 0) {
      timer.value -= 1;
    } else {
      clearTimer();
    }
  }, 1000);
};

const sendVerificationCode = async () => {
  codeError.value = "";
  codeSent.value = false;
  codeLoading.value = true;
  try {
    codeSent.value = true;
    activeStep.value = 3; // Paso 3: Mostrar formulario de código
    timer.value = 120;
    startTimer();
  } catch (err) {
    codeError.value = "No se pudo enviar el código. Intenta de nuevo.";
  } finally {
    codeLoading.value = false;
  }
};

const otp = ref(Array(6).fill(""));

const focusNext = (index, event) => {
  if (event.target.value && index < otp.value.length - 1) {
    const nextInput = event.target.nextElementSibling;
    if (nextInput) nextInput.focus();
  }
};

const focusPrev = (index, event) => {
  if (!event.target.value && index > 0) {
    const prevInput = event.target.previousElementSibling;
    if (prevInput) prevInput.focus();
  }
};

const verifyCode = async () => {
  codeError.value = "";
  codeLoading.value = true;

  try {
    const code = otp.value.join("");
    const res = await authClient.activateUserByCode(code);

    // Éxito: res.status === 200 y res.data es tu string
    const mensajeExito = res.data;
    Swal.fire("¡Cuenta activada!", mensajeExito, "success");

    codeVerified.value = true;
    clearTimer();
    activeStep.value = 4;

  } catch (err) {
    // Aquí llegan 4xx/5xx con JSON { statusCode, message, error }
    const httpMessage = err.response?.data?.message;
    codeError.value = httpMessage || "Error inesperado. Intenta de nuevo.";
  } finally {
    codeLoading.value = false;
  }
};

 const resendCode = async () => {
   resendLoading.value = true;
   codeError.value = "";
  /* verificationCode.value = ""; */
  // Limpia el OTP previo
  otp.value = Array(8).fill("");
  codeVerified.value = false;
  codeSent.value = false;
   try {
     const mensaje = await authClient.resendActivationCode(email.value);
    // Tras respuesta exitosa, muestra mensaje y reactiva inputs
    Swal.fire("Listo", mensaje, "success");
    codeSent.value = true;
    timer.value = 120;
    startTimer();
   } catch (err) {
     codeError.value = "No se pudo reenviar el código. Intenta de nuevo.";
   } finally {
     resendLoading.value = false;
   }
 };

const invalidFields = ref({});

const validateForm = () => {
  errors.value = [];
  invalidFields.value = {};

  // Validate required fields
  if (!name.value.trim()) {
    errors.value.push("El nombre es obligatorio.");
    invalidFields.value.name = true;
  }
  if (!lastName.value.trim()) {
    errors.value.push("El apellido es obligatorio.");
    invalidFields.value.lastName = true;
  }
  if (!email.value.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
    errors.value.push("El correo electrónico no es válido.");
    invalidFields.value.email = true;
  }

  if (telefonoNumero.value && !telefonoNumero.value.match(/^[0-9]{8}$/)) {
    errors.value.push("El teléfono no es válido.");
    invalidFields.value.telefonoNumero = true;
  }

  if (!rolId.value && activeStep.value === 1) {
    errors.value.push("Debe seleccionar un rol");
    invalidFields.value.rolId = true;
  }

  if (activeStep.value === 2) {
    if (!password.value.trim()) {
      errors.value.push("La contraseña es obligatoria.");
      invalidFields.value.password = true;
    } else if (password.value.length < 6) {
      errors.value.push("La contraseña debe tener al menos 6 caracteres.");
      invalidFields.value.password = true;
    }

    if (password.value !== confirmPassword.value) {
      errors.value.push("Las contraseñas no coinciden.");
      invalidFields.value.confirmPassword = true;
    }
  }

  return errors.value.length === 0;
};

const nextStep = async () => {
  if (!validateForm()) return;

  // Al avanzar desde el paso 2 (contraseña) al 3 (verificación):
  if (activeStep.value === 2) {
    sendVerificationCode();    // → aquí seteas activeStep=3, timer y startTimer()
    return;                          // sales para no incrementar dos veces
  }

  // Para los demás pasos, avanza normalmente
  if (activeStep.value < 4) {
    activeStep.value += 1;
  }
};

const prevStep = () => {
  if (activeStep.value > 0) {
    activeStep.value -= 1;
  }
};

const handleSubmit = async () => {
  errors.value = [];
  message.value = "";
  loading.value = true;

  if (!validateForm()) {
    loading.value = false;
    return;
  }

  try {
    const fullName = `${name.value} ${lastName.value}`;

    // Call the updated RegisterUser function
    await RegisterUser(
      fullName,        // nombre (combined first + last name)
      email.value,     // email
      password.value,  // contrasena
      rolId.value,     // role_id
      telefonoNumero.value ? parseInt(telefonoNumero.value) : undefined // telefono (optional)
    );

    

    // Swal.fire({
    //   icon: "success",
    //   title: "¡Usuario registrado exitosamente!",
    //   text: `El usuario ${email.value} ha sido creado.`,
    //   customClass: {
    //     confirmButton: "btn bg-gradient-success",
    //   },
    //   buttonsStyling: false,
    // });

    // emit("close");
    // closeModal();

    // // Muestra el sidenav después del registro
    // store.commit('setShowSidenav', true);
    
    // router.push({ name: "Login" });

  } catch (error) {
    console.log("Registration error:", error.message);
    let errorMessage = error.response?.data?.message || "Error al registrar el usuario";

    if (error.message.includes("correo")) {
      errorMessage = "El correo electrónico ya está registrado";
    }

    Swal.fire({
      icon: "error",
      title: "Error",
      text: errorMessage,
      customClass: {
        confirmButton: "btn bg-gradient-danger",
      },
      buttonsStyling: false,
    });
  } finally {
    loading.value = false;
  }
};

const closeModal = () => {
  emit("close");
  message.value = "";
  errors.value = [];
  name.value = "";
  lastName.value = "";
  email.value = "";
  telefonoNumero.value = "";
  password.value = "";
  confirmPassword.value = "";

  if (route.query.from === "sidebar") {
    router.back();
  }
};

const redirectToLogin = () => {
  router.push({ name: "Login" }); // Asegúrate de que "Login" sea el nombre de la ruta de inicio de sesión
};

onMounted(() => {
  store.commit('setShowSidenav', false);
});

onUnmounted(() => clearTimer());

</script>

<template>
  <main class="main-content main-content-bg mt-0">
    <div class="page-header min-vh-95" :style="{
      backgroundImage: `url(${require('@/assets/img/login-bg.png')})`
    }">

      <span class="mask bg-gradient-dark opacity-6"></span>
      <div class="container">
        <div class="row justify-content-center align-items-center">
          <div class="col-lg-8 col-md-7">
            <div class="card z-index-0">
              <div class="card-header text-center pt-4">
                <h5 class="mb-0">Registro</h5>
              </div>
              <div class="p-4 mt-0 mt-lg-0 pt-0 pt-lg-0">
                <div class="row h-70 h-lg-100">
                  <!-- Left card section with step icon -->
                  <div class="col-lg-4 col-md-6 col-12">
                    <div class="step-card d-flex flex-column justify-content-center align-items-center text-center">
                      <div class="step-icon">
                        <i class="far fa-id-card" :class="stepIconClass"></i>
                      </div>

                      <div class="progress mb-3 mt-4 w-100">
                        <div class="progress-bar bg-gradient-dark" role="progressbar"
                          :style="{ width: ((activeStep + 1) / 5) * 100 + '%' }" aria-valuenow="10" aria-valuemin="0"
                          aria-valuemax="100"></div>
                      </div>
                      <p class="mb-0">{{ activeStep + 1 }} de 5</p>
                    </div>
                  </div>

                  <!-- Right side with form -->
                  <div class="col-lg-8 col-md-6 col-12">

                    <form @submit.prevent="handleSubmit">
                      <!-- <form action=""> -->
                      <div class="modal-body">
                        <!-- Step 1: Datos Personales -->
                        <div v-if="activeStep === 0">
                          <div class="row pe-3 list-row">
                            <div class="col-lg-1 col-12">
                              <i class="fas fa-portrait fa-lg me-2 mt-2"></i>
                            </div>
                            <div class="col-lg-4 col-12">
                              <input v-model="name" type="text" class="form-control mb-3"
                                :class="{ 'is-invalid': invalidFields.name }" placeholder="Nombre*" required />
                            </div>
                            <div class="col-lg-7 col-12">
                              <input v-model="lastName" type="text" class="form-control mb-3"
                                :class="{ 'is-invalid': invalidFields.lastName }" placeholder="Apellidos*" required />
                            </div>
                          </div>

                          <hr class="horizontal dark mt-1 mb-4" />

                          <div class="row pe-3 list-row">
                            <div class="col-lg-1 col-12">
                              <i class="fas fa-phone-square-alt fa-lg me-2 mt-2"></i>
                            </div>
                            <div class="col-lg-6 col-12">
                              <input v-model="telefonoNumero" type="text" class="form-control mb-3"
                                :class="{ 'is-invalid': invalidFields.telefonoNumero }"
                                placeholder="Número de Teléfono*" required />
                              <strong v-if="invalidFields.telefonoNumero" class="text-danger text-xs">*El numero de
                                teléfono no es
                                válido</strong>
                            </div>
                          </div>
                          <div class="row pe-3 list-row">
                            <div class="col-lg-1 col-12">
                              <i class="fas fa-envelope me-2 fa-lg me-2 mt-2"></i>
                            </div>
                            <div class="col-lg-6 col-12">
                              <input v-model="email" type="text" class="form-control mb-0"
                                :class="{ 'is-invalid': invalidFields.email }" placeholder="Correo Electrónico*"
                                required />
                              <strong v-if="invalidFields.email" class="text-danger text-xs">*El correo electrónico no
                                es
                                válido</strong>
                            </div>
                          </div>
                        </div>

                        <!-- Step 2: Permisos y Roles -->
                        <div v-if="activeStep === 1">
                          <div class="row pe-3 list-row icons justify-content-center">
                            <p class="mb-0 text-sm">Seleccione un rol:</p>
                          </div>
                          <div class="row pe-3 my-3 list-row icons justify-content-center">
                            <div class="col-5 d-flex flex-column align-items-center" v-for="rol in roles" :key="rol.id">
                              <div class="d-md-flex align-items-center w-100">
                                <div class="role-option me-2" :class="{
                                  selected: rolId === rol.id,
                                  'is-invalid': invalidFields.rolId,
                                }" @click="rolId = rol.id">
                                  <i class="fas" :class="rol.id === 3 ? 'fa-chalkboard-teacher' : 'fa-user'"></i>
                                </div>
                                <span :class="{ 'active-label': rolId === rol.id }">{{ rol.nombre }}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <!-- Step 3: Contraseña -->
                        <div v-if="activeStep === 2">
                          <input v-model="password" type="password" class="form-control mb-3 w-80 w-md-50 pe-5"
                            placeholder="Contraseña" :class="{ 'is-invalid': invalidFields.password }" required />
                          <div v-if="invalidFields.password" class="invalid-feedback text-xs mt-0">
                            Las contraseñas no cumple con los requisitos.
                          </div>
                          <input v-model="confirmPassword" type="password" class="form-control mb-3 w-80 w-md-50 pe-5"
                            placeholder="Confirmar Contraseña" :class="{ 'is-invalid': invalidFields.confirmPassword }"
                            required />
                          <div v-if="invalidFields.confirmPassword" class="invalid-feedback text-xs mt-0">
                            Las contraseñas no coinciden.
                          </div>
                          <hr class="horizontal dark mt-4 mb-4" />

                          <small class="text-muted"> *La contraseña debe contener: </small>
                          <div class="row list-row w-100">
                            <div class="col-lg-6 col-12 ps-0">
                              <ul class="password-requirements">
                                <li>8 caracteres como mínimo</li>
                                <li>Una letra mayúscula.</li>
                                <li>Una letra minúscula.</li>
                              </ul>
                            </div>
                            <div class="col-lg-6 col-12 ps-0">
                              <ul class="password-requirements">
                                <li>Un número.</li>
                                <li>Un carácter especial (ej. !, @, #, etc.).</li>
                              </ul>
                            </div>
                          </div>
                        </div>

                        <!-- Paso 4: Código de verificación -->
                        <div v-if="activeStep === 3">
                          <div class="mb-3">
                            <label class="form-label">Se ha enviado un código de verificación a tu correo electrónico.</label>
                            <div class="otp-input-container">
                              <input
                                v-for="(digit, index) in otp"
                                :key="index"
                                type="text"
                                maxlength="1"
                                class="otp-input"
                                v-model="otp[index]"
                                @input="focusNext(index, $event)"
                                @keydown.backspace="focusPrev(index, $event)"
                                :disabled="!codeSent || timer === 0 || codeVerified"
                              />
                            </div>
                            <div class="mt-2">
                              <span v-if="timer > 0 && !codeVerified" class="text-muted small d-flex align-items-center">
                                <i class="fas fa-spinner fa-spin me-2"></i> <!-- Spinner animado -->
                                Código expira en: {{ formattedTimer }}
                              </span>
                              <span v-if="timer === 0 && !codeVerified" class="text-danger small">
                                El código ha expirado.
                              </span>
                            </div>
                            <div v-if="codeError" class="text-danger mt-2 small">{{ codeError }}</div>
                            <!-- Botón Verificar -->
                            <button
                              v-if="timer > 0"
                              class="btn btn-dark mt-3"
                              :disabled="timer === 0 || codeLoading || codeVerified || otp.join('').length !== 6"
                              @click.prevent="verifyCode"
                            >
                              {{ codeLoading ? "Verificando..." : "Verificar" }}
                            </button>
                            <!-- Botón Reenviar Código -->
                            <button
                              v-if="timer === 0 && !codeVerified"
                              class="btn btn-info mt-2"
                              :disabled="resendLoading"
                              @click.prevent="resendCode"
                            >
                              {{ resendLoading ? "Enviando..." : "Reenviar código" }}
                            </button>
                          </div>
                        </div>

                        <!-- Paso 5: Confirmación final -->
                        <div v-if="activeStep === 4">
                          <div class="alert alert-success">
                            <i class="fas fa-check-circle me-2"></i>
                            Código verificado correctamente. Tu registro ha sido completado.
                          </div>
                          <!-- <button class="btn btn-dark" type="submit">
                            Registrar
                          </button> -->
                        </div>
                      </div>

                      <div class="modal-footer">
                        <div class="d-flex justify-content-between w-100">
                          <div class="col-lg-6 col-4 ps-0">
                            <small class="text-muted">
                              <i class="fas fa-asterisk text-dark fa-sm pe-1"></i>
                              Requerido
                            </small>
                          </div>
                          <div class="col-lg-6 col-7 ps-0 text-end">
                            <argon-button type="button" color="secondary" class="me-3" @click="prevStep"
                              v-if="activeStep > 0 && activeStep < 3"><i class="fas fa-chevron-left fa-lg"></i></argon-button>
                            <!-- <argon-button type="button" color="dark" @click="nextStep" v-if="activeStep < 4"><i
                                class="fas fa-chevron-right fa-lg"></i></argon-button> -->
                            <argon-button
                              :disabled="loading"
                              type="button"
                              color="dark"
                              variant="gradient"
                              v-if="activeStep === 4"
                              @click="redirectToLogin"
                            >
                              {{ loading ? "Redirigiendo..." : "Iniciar Sesión" }}
                            </argon-button>
                            <argon-button
                              type="button"
                              color="dark"
                              @click.prevent="onNextClick"
                              v-if="activeStep < 3"
                            >
                              <i class="fas fa-chevron-right fa-lg"></i>
                            </argon-button>
                          </div>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<style scoped>
.step-card {
  padding: 20px;
  background: #344767;
  border-radius: 8px;
  text-align: center;
  box-shadow: 0 0 30px rgba(115, 255, 0, 0.1);
  height: 100%;
}

.step-icon {
  font-size: 100px;
  color: #fff;
}

.step-card p {
  font-size: 1rem;
  color: #fff;
}

.list-row .col-lg-6 {
  margin-bottom: 15px;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 24, 6, 0.384);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.list-row.icons {
  column-gap: 3rem;
}

.modal-content {
  background: #fff;
  border-radius: 8px;
  max-width: 900px;
  height: 460px;
  width: 90%;
  padding: 20px;
  position: relative;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #dee2e6;
}

.modal-body {
  height: 330px;
  overflow-y: auto;
}

.modal-footer {
  height: 60px;
  gap: 10px;
  margin-top: auto;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #000;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.btn-close::before {
  content: "×";
  font-weight: bold;
}

.btn-close:hover::before {
  color: #ff0000;
}

input.form-control:focus {
  border-bottom: 4px solid #4e4e4e;
}

.otp-input-container {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 15px;
}

.otp-input {
  width: 50px;
  height: 50px;
  text-align: center;
  font-size: 1.5rem;
  border: 2px solid #ccc;
  border-radius: 8px;
  outline: none;
  transition: border-color 0.3s ease;
}

.otp-input:focus {
  border-color: #344767;
  box-shadow: 0 0 5px rgba(52, 71, 103, 0.5);
}

.otp-input::placeholder {
  color: #ddd;
}

.otp-input:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .modal-content {
    width: 95%;
    max-width: 400px;
    padding: 15px;
    height: 700px;
  }

  .modal-header {
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
  }

  .list-row.icons {
    column-gap: 1rem;
  }

  .modal-title {
    font-size: 1rem;
    margin-top: 15px;
  }

  .btn-close {
    font-size: 1.2rem;
    align-self: flex-end;
  }

  .modal-footer {
    flex-direction: column;
    align-items: stretch;
    padding: 0%;
  }

  .argon-button {
    width: 100%;
  }

  .step-icon {
    font-size: 30px;
    color: #fff;
  }

  .step-card {
    flex-direction: row !important;
    justify-content: space-between !important;
    padding-top: 0%;
    padding-bottom: 0%;
  }

  .progress {
    width: 50% !important;
    margin-top: 1rem !important;
    margin-bottom: 1rem !important;

  }

  .modal-body {
    height: 410px;
    overflow-y: auto;
  }

  .role-option {
    margin-top: 10px !important;
    width: 60px !important;
    height: 60px !important;
  }


}

.role-option {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  border: 2px solid #ccc;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  margin: 10px;
  margin-top: 0px;
  margin-bottom: 3px;
  font-size: 24px;
  position: relative;
}

.role-option i {
  color: #555;
}

.role-labels {
  display: flex;
  justify-content: center;
  gap: 4rem;
  width: 100%;
}

.role-labels span {
  font-size: 14px;
  color: #777;
}

.role-option.selected {
  border: 3px solid #344767;
  background-color: rgba(45, 206, 137, 0.1);
}

.active-label {
  font-weight: bold;
  color: #2dce89;
}

.password-requirements {
  list-style-type: none;
  padding-left: 25px;
  margin-top: 2px;
}

.password-requirements li {
  font-size: 0.75rem;
  color: #6c757d;
  margin-bottom: 2px;
  display: flex;
  align-items: center;
}

.password-requirements li::before {
  content: "•";
  color: #2dce89;
  font-size: 1.2rem;
  margin-right: 8px;
  display: inline-block;
}

.is-invalid {
  border-bottom: 2px solid red !important;
  background: linear-gradient(to top, #faecec, #ffffff);
  transition:
    0.3s ease-in-out,
    border 0.3s ease-in-out;
}

.fa-spinner {
  font-size: 0.9rem; /* Ajusta el tamaño del spinner */
  color: #6c757d; /* Color del spinner */
}
</style>
